import { Button, Input, Label, Switch, Textarea } from "@devhop/ui";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { FORM_FIELD_TYPE_DEFINITIONS } from "../constants/form-field-type-definitions";
import type { FormField, FormFieldType } from "../types/form-builder";

function mergeConfigOnTypeChange(
  prev: FormField,
  newType: FormFieldType,
): FormField["config"] {
  const next: FormField["config"] = { ...prev.config };
  const newNeedsOptions = ["select", "multiselect", "radio"].includes(newType);
  if (!newNeedsOptions) {
    delete next.options;
  }
  if (newNeedsOptions && (!next.options || next.options.length === 0)) {
    next.options = [
      { label: "ຕົວເລືອກ 1", value: "option1" },
      { label: "ຕົວເລືອກ 2", value: "option2" },
    ];
  }
  return next;
}

type FieldPropertiesPanelProps = {
  field: FormField;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
};

export function FieldPropertiesPanel({
  field,
  onUpdateField,
}: FieldPropertiesPanelProps) {
  const [label, setLabel] = useState(field.label);
  const [placeholder, setPlaceholder] = useState(field.placeholder || "");
  const [required, setRequired] = useState(field.required);
  const [options, setOptions] = useState(
    field.config.options?.map((o) => `${o.label}|${o.value}`).join("\n") || "",
  );

  useEffect(() => {
    setLabel(field.label);
    setPlaceholder(field.placeholder || "");
    setRequired(field.required);
    setOptions(
      field.config.options?.map((o) => `${o.label}|${o.value}`).join("\n") ||
        "",
    );
  }, [field]);

  const handleUpdate = () => {
    const updates: Partial<FormField> = {
      label,
      placeholder: placeholder || undefined,
      required,
    };

    // Parse options for select/multiselect/radio
    if (
      ["select", "multiselect", "radio"].includes(field.type) &&
      options.trim()
    ) {
      const parsedOptions = options
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => {
          const parts = line.split("|");
          return {
            label: parts[0]?.trim() || parts[0] || "",
            value: parts[1]?.trim() || parts[0]?.trim() || "",
          };
        });
      updates.config = {
        ...field.config,
        options: parsedOptions,
      };
    }

    onUpdateField(field.id, updates);
  };

  const needsOptions = ["select", "multiselect", "radio"].includes(field.type);

  return (
    <div
      className="rounded-xl border bg-card p-4"
      data-tourid="field-properties-panel"
    >
      <h3 className="mb-4 font-semibold">ຄຸນສົມບັດຊ່ອງຂໍ້ມູນ</h3>
      <div className="space-y-4">
        <div>
          <Label htmlFor="field-type">ປະເພດຊ່ອງຂໍ້ມູນ</Label>
          <SimpleSelect
            id="field-type"
            value={field.type}
            onValueChange={(val) => {
              const newType = val as FormFieldType;
              if (newType === field.type) return;
              onUpdateField(field.id, {
                type: newType,
                config: mergeConfigOnTypeChange(field, newType),
              });
            }}
            placeholder="ເລືອກປະເພດ"
            className="mt-1 w-full"
            options={FORM_FIELD_TYPE_DEFINITIONS.map((d) => ({
              value: d.type,
              label: d.label,
            }))}
          />
          <p className="mt-1 text-muted-foreground text-xs">
            {FORM_FIELD_TYPE_DEFINITIONS.find((d) => d.type === field.type)
              ?.description ?? ""}
          </p>
        </div>

        <div>
          <Label htmlFor="field-label">ຊື່ຊ່ອງຂໍ້ມູນ</Label>
          <Input
            id="field-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={handleUpdate}
            placeholder="ຕົວຢ່າງ: ຊື່-ນາມສະກຸນ"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="field-placeholder">ຂໍ້ຄວາມຊີ້ແຈງ</Label>
          <Input
            id="field-placeholder"
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            onBlur={handleUpdate}
            placeholder="ຕົວຢ່າງ: ປ້ອນຊື່ຂອງທ່ານ"
            className="mt-1"
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="field-required">ບັງຄັບຕ້ອງການ</Label>
          <Switch
            id="field-required"
            checked={required}
            onCheckedChange={(checked) => {
              setRequired(checked);
              onUpdateField(field.id, { required: checked });
            }}
          />
        </div>

        {needsOptions && (
          <div>
            <Label htmlFor="field-options">ຕົວເລືອກ (ແຕ່ລະແຖວ, ຮູບແບບ: ຊື່|ຄ່າ)</Label>
            <Textarea
              id="field-options"
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              onBlur={handleUpdate}
              placeholder="ຕົວຢ່າງ:&#10;ຕົວເລືອກ 1|value1&#10;ຕົວເລືອກ 2|value2"
              className="mt-1 min-h-24 font-mono text-xs"
              rows={6}
            />
            <p className="mt-1 text-muted-foreground text-xs">
              ແຕ່ລະແຖວ = 1 ຕົວເລືອກ, ໃຊ້ | ເພື່ອແຍກຊື່ແລະຄ່າ
            </p>
          </div>
        )}

        <div className="pt-2">
          <Button onClick={handleUpdate} className="w-full" size="sm">
            ບັນທຶກການປ່ຽນແປງ
          </Button>
        </div>
      </div>
    </div>
  );
}
